// src/hooks/useRecordData.ts
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { RootState } from "../../store/store";
import fetchWrapper from "../../api/fetchWrapper";
import {
  buildFormBody,
  formatRecordData,
  getEndpoint,
  isValidFilter,
} from "../../utils/helper";
import { setRefresh } from "../../store/slices/recordSlice";

export const useRecordData = ({
  needsReview = false,
  noRisk = false,
  dataLimit,
}: any) => {
  const dispatch = useDispatch();
  const [recordData, setRecordData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const { selectedFilter } = useSelector((state: RootState) => state.filter);
  const queryClient = useQueryClient();
  const pollCount = useRef(0);
  const pollInterval = useRef<NodeJS.Timeout | null>(null);

  const endpoint = useMemo(
    () => getEndpoint(needsReview, noRisk),
    [needsReview, noRisk]
  );

  const isFilterValid = useMemo(
    () => isValidFilter(selectedFilter, endpoint, dataLimit),
    [selectedFilter, endpoint, dataLimit]
  );

  const formBody = useMemo(
    () => buildFormBody(selectedFilter, dataLimit),
    [selectedFilter, dataLimit]
  );

  const {
    data = [],
    isLoading: queryLoading,
    isError,
  } = useQuery({
    queryKey: ["recordData", selectedFilter, dataLimit, endpoint],
    enabled: isFilterValid,
    queryFn: async ({ signal }) => {
      const response = await fetchWrapper(endpoint, {
        method: "POST",
        body: formBody,
        signal,
      });

      if (!response || response.status === 500) {
        throw new Error("Internal Server Error");
      }

      return response;
    },
    retry: false,
  });

  useEffect(() => {
    if (!queryLoading && dataLimit) {
      if (data?.message || isError) {
        setRecordData([]);
      } else {
        const newData = formatRecordData(data?.data);
        setRecordData((prev: any) => {
          if (dataLimit[0] === 0) return newData;
          const byRecordId = new Map<string, any>();
          [...prev, ...newData].forEach((row: any) =>
            byRecordId.set(row.recordId, row)
          );
          return Array.from(byRecordId.values());
        });
      }
    }
  }, [data, dataLimit, queryLoading, isError]);

  useEffect(() => {
    if (!queryLoading) setTotalCount(data?.filtered_record_count ?? 0);
  }, [data, queryLoading]);

  const poll = (retries = 2) => {
    if (pollInterval.current) return;
    setIsPolling(true);
    pollCount.current = 0;

    pollInterval.current = setInterval(async () => {
      if (pollCount.current >= retries - 1) {
        clearInterval(pollInterval.current!);
        pollInterval.current = null;
        await queryClient.refetchQueries({
          queryKey: ["recordData", selectedFilter, dataLimit, endpoint],
          type: "active",
        });
        setIsPolling(false);
        dispatch(setRefresh(false));
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ["recordData", selectedFilter, dataLimit, endpoint],
      });

      pollCount.current += 1;
    }, 5);
  };

  const initVal = useMemo(
    () => ({
      recordData: [],
      isLoading: false,
      isError: false,
      warning: "",
      totalCount: 0,
      poll: null,
    }),
    []
  );

  return !isFilterValid
    ? initVal
    : {
        recordData,
        isLoading: queryLoading || isPolling,
        isError,
        warning: data?.message,
        totalCount,
        poll,
      };
};
